import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { getHealth, getEngineStatus, getCustomers } from '../../api'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import './AdminDashboard.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'

function VannaTrainingCard({ customers }) {
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [training, setTraining] = useState(false)
  const [result, setResult] = useState(null)

  const handleTrain = async (force) => {
    if (!selectedCustomer) return
    setTraining(true)
    setResult(null)
    try {
      const API = API_BASE_CONFIG
      const res = await fetch(
        `${API}/api/admin/train/comprehensive?customer_id=${selectedCustomer}&force_retrain=${force}`,
        { method: 'POST' }
      )
      const data = await res.json()
      setResult(data)
    } catch (err) {
      setResult({ success: false, error: err.message })
    } finally {
      setTraining(false)
    }
  }

  return (
    <div className="ad-card">
      <h3 className="ad-card-title">Vanna Training</h3>
      <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
        Rebuild NL-to-SQL training from actual customer data. Generates Q&A pairs for every categorical column, value-specific queries, and aggregates.
      </p>
      <select
        value={selectedCustomer}
        onChange={e => { setSelectedCustomer(e.target.value); setResult(null) }}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 13, fontFamily: 'inherit', marginBottom: 10 }}
      >
        <option value="">Select customer…</option>
        {customers.map((c, i) => (
          <option key={i} value={c.customer_id}>{c.company_name || c.customer_id}</option>
        ))}
      </select>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => handleTrain(false)}
          disabled={!selectedCustomer || training}
          className="ad-train-btn"
        >
          {training ? 'Training…' : 'Train (skip if exists)'}
        </button>
        <button
          onClick={() => handleTrain(true)}
          disabled={!selectedCustomer || training}
          className="ad-train-btn force"
        >
          {training ? 'Training…' : 'Force Rebuild'}
        </button>
      </div>
      {result && (
        <div className={`ad-train-result ${result.success ? 'success' : 'error'}`}>
          {result.skipped
            ? `Already trained (${result.existing_pairs} pairs). Use Force Rebuild to redo.`
            : result.success
              ? `Trained ${result.pairs_trained} of ${result.pairs_generated} pairs.`
              : `Error: ${result.error || 'Unknown'}`
          }
        </div>
      )}
    </div>
  )
}

function PurgeWorkEffortsCard({ customers }) {
  const [selectedCustomer, setSelectedCustomer] = useState('')
  const [purging, setPurging] = useState(false)
  const [result, setResult] = useState(null)

  const handlePurge = async () => {
    if (!selectedCustomer) return
    if (!window.confirm(`Delete ALL work efforts for ${selectedCustomer}?`)) return
    setPurging(true)
    setResult(null)
    try {
      const API = API_BASE_CONFIG
      const res = await fetch(
        `${API}/api/admin/purge/work-efforts/${selectedCustomer}`,
        { method: 'DELETE' }
      )
      const data = await res.json()
      setResult(data)
    } catch (err) {
      setResult({ success: false, error: err.message })
    } finally {
      setPurging(false)
    }
  }

  return (
    <div className="ad-card">
      <h3 className="ad-card-title">Purge Work Efforts</h3>
      <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 12 }}>
        Delete all work efforts (investigations) for a customer.
      </p>
      <select
        value={selectedCustomer}
        onChange={e => { setSelectedCustomer(e.target.value); setResult(null) }}
        style={{ width: '100%', padding: '6px 8px', marginBottom: 10, fontSize: 13, borderRadius: 6, border: '1px solid var(--gray-300)' }}
      >
        <option value="">Select customer...</option>
        {customers.map(c => (
          <option key={c.id || c.customer_id} value={c.customer_id || c.id}>
            {c.display_name || c.customer_id || c.id}
          </option>
        ))}
      </select>
      <button
        onClick={handlePurge}
        disabled={!selectedCustomer || purging}
        style={{ padding: '6px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid #c0334e', background: purging ? '#ccc' : '#f7e2e7', color: '#c0334e', cursor: selectedCustomer && !purging ? 'pointer' : 'default' }}
      >
        {purging ? 'Purging...' : 'Purge All'}
      </button>
      {result && (
        <div style={{ marginTop: 10, fontSize: 12, padding: '8px 10px', borderRadius: 6, background: result.success ? '#f0fdf4' : '#fbeef0', color: result.success ? '#166534' : '#991b1b' }}>
          {result.success ? `Deleted ${result.deleted} work efforts` : `Error: ${result.error}`}
        </div>
      )}
    </div>
  )
}

function AdminDashboard() {
  const [health, setHealth] = useState(null)
  const [engines, setEngines] = useState(null)
  const [customers, setCustomers] = useState([])
  // A failed request is shown as a failure, never as zero customers / zero tables (item 201).
  const [healthError, setHealthError] = useState(null)
  const [enginesError, setEnginesError] = useState(null)
  const [customersError, setCustomersError] = useState(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    try {
      const [h, e, c] = await Promise.allSettled([
        getHealth(),
        getEngineStatus(),
        getCustomers(),
      ])
      setHealth(h.status === 'fulfilled' ? h.value : null)
      setHealthError(h.status === 'fulfilled' ? null : loadErrorMessage(h.reason))
      setEngines(e.status === 'fulfilled' ? e.value : null)
      setEnginesError(e.status === 'fulfilled' ? null : loadErrorMessage(e.reason))
      const cust = settledList(c, 'contexts', 'customers')
      setCustomers(cust.items || [])
      setCustomersError(cust.error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const breadcrumbs = [{ label: 'Admin' }]
  const navActions = (
    <>
      <Link to="/admin/query" className="nav-btn">Query Inspector</Link>
      <Link to="/customers" className="nav-btn">Customers</Link>
      
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="ad-loading"><div className="ad-spinner" /><p>Loading admin dashboard…</p></div>
      </>
    )
  }

  const storage = health?.storage || {}
  const version = health?.version || '?'
  const status = health?.status || 'unknown'

  // Engine list from response
  const engineList = engines?.engines
    ? (Array.isArray(engines.engines) ? engines.engines : Object.entries(engines.engines).map(([name, data]) => ({ name, ...data })))
    : []

  const collections = storage.chromadb?.collections || {}

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="ad-page">
        <div className="ad-container">

        {healthError && <LoadError what="system health" error={healthError} onRetry={load} />}
        {enginesError && <LoadError what="engine status" error={enginesError} onRetry={load} />}
        {customersError && <LoadError what="customers" error={customersError} onRetry={load} />}

        {/* System Status Hero */}
        <div className="ad-hero">
          <div className="ad-hero-left">
            <div className={`ad-status-dot ${status === 'healthy' ? 'green' : 'red'}`} />
            <div>
              <h1 className="ad-hero-title">System {healthError ? 'Status Unknown' : status === 'healthy' ? 'Healthy' : 'Degraded'}</h1>
              <p className="ad-hero-meta">v{version} · {health?.environment || 'unknown'} · {health?.timestamp?.split('T')[0] || ''}</p>
            </div>
          </div>
          <div className="ad-hero-stats">
            <div className="ad-hero-stat">
              <div className="ad-hero-stat-value">{customersError ? '—' : customers.length}</div>
              <div className="ad-hero-stat-label">Customers</div>
            </div>
            <div className="ad-hero-stat">
              <div className="ad-hero-stat-value">{healthError ? '—' : storage.duckdb?.table_count || 0}</div>
              <div className="ad-hero-stat-label">Tables</div>
            </div>
            <div className="ad-hero-stat">
              <div className="ad-hero-stat-value">{healthError ? '—' : Object.values(collections).reduce((s, v) => s + (v || 0), 0)}</div>
              <div className="ad-hero-stat-label">Vectors</div>
            </div>
          </div>
        </div>

        <div className="ad-grid">
          {/* Storage Systems */}
          <div className="ad-card">
            <h3 className="ad-card-title">Storage Systems</h3>
            <div className="ad-storage-list">
              {['duckdb', 'chromadb', 'supabase'].map(key => {
                const sys = storage[key]
                if (!sys) return null
                return (
                  <div key={key} className="ad-storage-item">
                    <div className={`ad-storage-dot ${sys.healthy ? 'green' : 'red'}`} />
                    <div className="ad-storage-info">
                      <div className="ad-storage-name">{key.toUpperCase()}</div>
                      <div className="ad-storage-meta">
                        {key === 'duckdb' && `${sys.table_count || 0} tables · ${sys.db_path || ''}`}
                        {key === 'chromadb' && `${Object.keys(sys.collections || {}).length} collections · hierarchy=${sys.hierarchy_support ? 'yes' : 'no'}`}
                        {key === 'supabase' && `${sys.customer_count || 0} customers`}
                      </div>
                    </div>
                    <span className={`ad-storage-badge ${sys.healthy ? 'healthy' : 'error'}`}>
                      {sys.healthy ? 'Healthy' : 'Error'}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* ChromaDB Collections */}
          <div className="ad-card">
            <h3 className="ad-card-title">Vector Collections</h3>
            <div className="ad-collection-list">
              {Object.entries(collections).map(([name, count]) => (
                <div key={name} className="ad-collection-item">
                  <span className="ad-collection-name">{name.replace('xlr8_', '')}</span>
                  <span className="ad-collection-count">{count} entries</span>
                </div>
              ))}
            </div>
          </div>

          {/* Engines */}
          {engineList.length > 0 && (
            <div className="ad-card ad-card-wide">
              <h3 className="ad-card-title">Engines</h3>
              <div className="ad-engine-grid">
                {engineList.map((eng, i) => (
                  <div key={i} className="ad-engine-item">
                    <div className={`ad-engine-dot ${eng.status === 'healthy' || eng.healthy ? 'green' : 'amber'}`} />
                    <div className="ad-engine-name">{eng.name || eng.engine || `Engine ${i + 1}`}</div>
                    {eng.response_time_ms && (
                      <div className="ad-engine-time">{eng.response_time_ms}ms</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customers */}
          <div className="ad-card">
            <h3 className="ad-card-title">Customers</h3>
            {customersError && <LoadError inline what="customers" error={customersError} onRetry={load} />}
            <div className="ad-customer-list">
              {customers.map((c, i) => (
                <Link key={i} to={`/customers/${c.customer_id}`} className="ad-customer-item">
                  <span className="ad-customer-name">{c.company_name}</span>
                  <span className="ad-customer-meta">
                    {c.employee_count?.toLocaleString() || '?'} emp · {c.industry || ''}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="ad-card">
            <h3 className="ad-card-title">Quick Links</h3>
            <div className="ad-links">
              <Link to="/admin/query" className="ad-link">🔍 Query Inspector</Link>
              <Link to="/admin/knowledge" className="ad-link">📚 Knowledge Base</Link>
              <Link to="/admin/security" className="ad-link">🔐 Security & Users</Link>
              <Link to="/admin/roles" className="ad-link">🎭 Role Configuration</Link>
              <Link to="/customers" className="ad-link">👥 Customer List</Link>
              <Link to="/threads" className="ad-link">🔗 Thread Library</Link>
              <Link to="/exports/templates" className="ad-link">📄 Export Templates</Link>
              <Link to="/admin/observations" className="ad-link">⚡ Observation Engine</Link>
                <Link to="/admin/rules" className="ad-link">📐 Knowledge &amp; Rules</Link>
              <Link to="/admin/schema-diff" className="ad-link">🔀 Schema Diff Review</Link>
              <Link to="/admin/graph" className="ad-link" title="How a customer's tables connect and join">🧩 Context Graph</Link>
              <Link to="/admin/paycode-crosswalk" className="ad-link">🧾 Paycode Crosswalk</Link>
              <Link to="/admin/dev-hub-capture" className="ad-link">🌐 Developer Hub Capture</Link>
              <Link to="/admin/templates" className="ad-link" title="Report export templates shown as vendor workbook cards; the column mapping it shows is sample data">📋 Template Library</Link>
              <Link to="/admin/onboarding" className="ad-link" title="A customer's onboarding checklist, our RaaS report definitions, and the access requests to hand them">🧭 Onboarding</Link>
              <Link to="/admin/connectors" className="ad-link" title="Every customer's system connections and how their last sync went, on one page">🔌 Connectors</Link>
              <Link to="/admin/memory" className="ad-link" title="A customer's stored session, project and global memory, shown as raw JSON">🧠 Memory Viewer</Link>
              <Link to="/admin/vendors" className="ad-link" title="Every vendor that has a schema definition on the platform, searchable by name">🏷️ Vendor Library</Link>
              <Link to="/admin/compliance" className="ad-link" title="Compliance rules by jurisdiction, with each rule's summary">⚖️ Compliance Rules</Link>
            </div>
          </div>

          {/* Vanna Training */}
          <VannaTrainingCard customers={customers} />

          {/* Purge Work Efforts */}
          <PurgeWorkEffortsCard customers={customers} />
        </div>
        </div>
      </div>
    </>
  )
}


export default AdminDashboard
